import React, { useEffect, useState } from "react";
import { Camera, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "../lib/api";
import DropLocationPicker from "./DropLocationPicker";

// FLD-10 — owner-furnished material. Shared by the warehouse ticket, the delivery view and Install › Material.
export const OFCI_TONE = {
  "OFCI — Expected Onsite": "bg-amber-100 text-amber-900 border-amber-300",
  "OFCI — Verified Onsite": "bg-emerald-100 text-emerald-800 border-emerald-300",
  "OFCI — Partially Verified — Missing Onsite": "bg-orange-100 text-orange-900 border-orange-300",
  "OFCI — Not Found Onsite": "bg-red-100 text-red-800 border-red-300",
  "OFCI — ABS Warehouse Custody": "bg-blue-100 text-blue-900 border-blue-300",
};
export const canTakeCustody = (role) => ["admin", "management", "warehouse", "foreman"].includes(String(role || "").toLowerCase());
const errText = (e, fb) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || fb; };

export const OfciBadge = ({ status, testId }) => (
  <span className={`inline-flex items-center px-1 rounded border text-[10px] font-semibold whitespace-nowrap ${OFCI_TONE[status] || "bg-amber-100 text-amber-900 border-amber-300"}`}
        title="Owner Furnished, Contractor Installed — not pulled from the ABS warehouse" data-testid={testId}>{status || "OFCI"}</span>
);

/** Verify Onsite / Track from ABS Warehouse buttons for one OFCI product (hidden once nothing is left to verify).
 *  verify=false on the warehouse ticket: the shop never verifies site material — the foreman or delivery crew does. */
export const OfciActions = ({ t, role, onVerify, onCustody, size = "sm", verify = true }) => {
  if (!t?.ofci || t.ofci_source === "abs_warehouse" || (t.remaining ?? t.ofci_remaining) <= 0) return null;
  const cls = size === "lg" ? "min-h-[44px] px-3 text-[13px]" : "h-6 px-1.5 text-[10px]";
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      {verify && <button type="button" onClick={() => onVerify(t)} data-testid={`ofci-verify-${t.product}`}
              className={`${cls} rounded border border-amber-400 bg-amber-50 font-semibold text-amber-900 whitespace-nowrap`}>Verify Onsite</button>}
      {canTakeCustody(role) && (
        <button type="button" onClick={() => onCustody(t)} data-testid={`ofci-custody-${t.product}`}
                className={`${cls} rounded border border-blue-300 bg-card font-semibold text-blue-900 whitespace-nowrap`}>Track from ABS Warehouse</button>
      )}
    </span>
  );
};

const EMPTY_LOC = { building: "", floor: "", area: "", room_key: "", room_label: "", note: "" };

/** target = { ticketId, projectId, key, line_ids, product, description, requested, verified, remaining } */
export function OfciVerifyDialog({ target, actor, onClose, onDone }) {
  const [qty, setQty] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [loc, setLoc] = useState(EMPTY_LOC);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setQty(String(target?.remaining ?? "")); setNotFound(false); setLoc(EMPTY_LOC); setNote(""); setPhotos([]); }, [target]);
  if (!target) return null;

  const addPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("project_id", target.projectId); fd.append("uploader", actor || ""); fd.append("file", file);
    setBusy(true);
    try { const { data } = await api.post("/completions/photos", fd, { headers: { "Content-Type": "multipart/form-data" } }); setPhotos((c) => [...c, data]); }
    catch (e) { toast.error(errText(e, "Could not upload the photo")); } finally { setBusy(false); }
  };
  const submit = async () => {
    const n = notFound ? 0 : Number(qty);
    if (!notFound && (!Number.isFinite(n) || n <= 0)) { toast.info("Enter the quantity found on site, or tick 'Not found onsite'"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/ofci/tickets/${target.ticketId}/verify`, {
        key: target.key || "", line_ids: target.line_ids || [], qty: n, not_found: notFound, by: actor, verified_on: date, note,
        location: { building: loc.building, floor: loc.floor, area: loc.area, room_key: loc.room_key, label: loc.note || loc.room_label || "" },
        photos: photos.map((p) => ({ storage_path: p.storage_path, filename: p.filename, content_type: p.content_type })),
      });
      toast[data.ofci_remaining > 0 || notFound ? "warning" : "success"](data.message);
      onDone?.(data); onClose();
    } catch (e) { toast.error(errText(e, "Could not save the verification")); } finally { setBusy(false); }
  };
  const inp = "min-h-[44px] sm:min-h-[36px] text-[14px] sm:text-[13px]";
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="ofci-verify-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2 flex-wrap">Verify OFCI material on site <OfciBadge status="OFCI — Expected Onsite" /></DialogTitle>
          <DialogDescription className="text-xs">
            <b className="text-foreground">{target.product}</b>{target.description ? ` — ${target.description}` : ""}<br />
            Requested <b>{target.requested}</b> · verified so far <b>{target.verified || 0}</b> · still expected on site <b data-testid="ofci-verify-remaining">{target.remaining}</b>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-[13px]">
          <div className="flex items-center gap-3 flex-wrap">
            <label className="flex items-center gap-1">Quantity found
              <Input type="number" min="0" step="any" value={qty} disabled={notFound} className={`${inp} w-24 text-right`} data-testid="ofci-verify-qty" onChange={(e) => setQty(e.target.value)} />
            </label>
            <label className="flex items-center gap-1.5 min-h-[44px] sm:min-h-0">
              <input type="checkbox" className="w-5 h-5 sm:w-4 sm:h-4" checked={notFound} data-testid="ofci-verify-notfound" onChange={(e) => setNotFound(e.target.checked)} />
              Not found onsite
            </label>
            <label className="flex items-center gap-1">Date
              <Input type="date" value={date} className={`${inp} w-40`} data-testid="ofci-verify-date" onChange={(e) => setDate(e.target.value)} />
            </label>
          </div>
          {!notFound && (
            <div className="rounded border border-border p-2 space-y-1" data-testid="ofci-verify-location">
              <div className="text-[11px] uppercase text-muted-foreground">Where is it? (pick a place or type it)</div>
              <DropLocationPicker projectId={target.projectId} value={loc} onChange={setLoc} />
              <Input placeholder="or describe the spot — e.g. loading dock, north stair" value={loc.note} className={inp}
                     data-testid="ofci-verify-label" onChange={(e) => setLoc((c) => ({ ...c, note: e.target.value }))} />
            </div>
          )}
          <Input placeholder={notFound ? "What did the owner / GC say? (optional)" : "Note (optional)"} value={note} className={inp}
                 data-testid="ofci-verify-note" onChange={(e) => setNote(e.target.value)} />
          <div className="flex items-center gap-2 flex-wrap">
            <label className="inline-flex items-center gap-1 min-h-[44px] sm:min-h-[32px] px-2 rounded border border-border bg-card cursor-pointer text-[12px]">
              <Camera className="w-4 h-4" /> Add photo
              <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="ofci-verify-photo" onChange={(e) => { addPhoto(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
            {photos.map((p, i) => (
              <span key={p.storage_path || i} className="inline-flex items-center gap-1 px-1.5 rounded bg-muted text-[11px]" data-testid={`ofci-verify-photo-${i}`}>
                {p.filename || "photo"}<button type="button" onClick={() => setPhotos((c) => c.filter((_, j) => j !== i))}><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <div className="text-[11px] text-muted-foreground">Verified by <b>{actor}</b>. Anything short of {target.remaining} is reported to the PM and foreman as missing on site.</div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="min-h-[44px] sm:min-h-[36px]" data-testid="ofci-verify-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="min-h-[44px] sm:min-h-[36px]" data-testid="ofci-verify-submit">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}{notFound ? "Report not found" : "Save verification"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function OfciCustodyDialog({ target, actor, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  if (!target) return null;
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/ofci/tickets/${target.ticketId}/custody`, { key: target.key || "", line_ids: target.line_ids || [], by: actor, note });
      toast.success(data.message); onDone?.(data); onClose();
    } catch (e) { toast.error(errText(e, "Could not change the custody")); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="ofci-custody-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm">Track {target.product} from the ABS warehouse?</DialogTitle>
          <DialogDescription className="text-xs">
            The <b>{target.remaining}</b> unit(s) not yet verified on site will be pulled and delivered by ABS like any other line.
            They stay flagged OFCI and never count as ABS-owned inventory. The PM and foreman are notified.
          </DialogDescription>
        </DialogHeader>
        <Input placeholder="Why? — e.g. owner shipped it to our shop (optional)" value={note} className="min-h-[44px] sm:min-h-[36px] text-[13px]"
               data-testid="ofci-custody-note" onChange={(e) => setNote(e.target.value)} />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="min-h-[44px] sm:min-h-[36px]" data-testid="ofci-custody-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="min-h-[44px] sm:min-h-[36px]" data-testid="ofci-custody-submit">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Take custody
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Both dialogs behind one piece of state: ofci = { kind: "verify" | "custody", target } */
export const OfciDialogs = ({ ofci, actor, onClose, onDone }) => (
  <>
    {ofci?.kind === "verify" && <OfciVerifyDialog target={ofci.target} actor={actor} onClose={onClose} onDone={onDone} />}
    {ofci?.kind === "custody" && <OfciCustodyDialog target={ofci.target} actor={actor} onClose={onClose} onDone={onDone} />}
  </>
);
